import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  axiosPut,
  reactQueryDefaultOptions,
  axiosGet,
  axiosPost,
  axiosDelete,
} from "../helpers/baseApi";
import { SERVICE_URLS } from "../helpers/serviceUrls";
import { QUERY_KEYS } from "../helpers/queryKeys";

export const useQueryGetActiveOEPeriod = (enabled = true) => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.CLIENT_ACTIVE_OE);
  };
  const options = {
    ...reactQueryDefaultOptions,
    enabled,
  };

  return useQuery([QUERY_KEYS.GET_CLIENTS_ACTIVE_OE], getData, options);
};

export const useQueryGetEmployeeCensusReport = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.EMPLOYEE_CENSUS_REPORT(oePeriodId, clientId),
    );
  };

  let enabled = false;
  if (oePeriodId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    refetchInterval: 120000,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.GET_CLIENTS, oePeriodId, clientId],
    getData,
    option,
  );
};

export const useQueryGetClientsStatusReport = (oePeriodId, showAllClients) => {
  const normalizedShowAllClients =
    showAllClients === undefined ? "No" : showAllClients;

  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_STATUS_REPORT(oePeriodId, normalizedShowAllClients),
    );
  };

  let enabled = false;
  if (oePeriodId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    refetchInterval: 120000,
    enabled: enabled,
  };

  return useQuery(
    [
      QUERY_KEYS.GET_CLIENTS_STATUS_REPORT,
      oePeriodId,
      normalizedShowAllClients,
    ],
    getData,
    option,
  );
};

export const useQueryGetClientsStatusReportPage = (
  oePeriodId,
  showAllClients,
  page,
  size,
  sort,
  search,
  statusFilter,
  renewalFilter,
  brokerFilter,
  benefitRepFilter,
  salesRepFilter,
  carrierFilter,
  waveFilter,
) => {
  return useQuery(
    [
      QUERY_KEYS.GET_CLIENTS,
      oePeriodId,
      showAllClients,
      page,
      size,
      sort,
      search,
      statusFilter,
      renewalFilter,
      brokerFilter,
      benefitRepFilter,
      salesRepFilter,
      carrierFilter,
      waveFilter,
    ],
    () =>
      axiosGet(
        SERVICE_URLS.CLIENT_STATUS_REPORT_PAGE(
          oePeriodId,
          showAllClients,
          page,
          size,
          sort,
          search,
          statusFilter,
          renewalFilter,
          brokerFilter,
          benefitRepFilter,
          salesRepFilter,
          carrierFilter,
          waveFilter,
        ),
      ),
    {
      keepPreviousData: true,
      enabled: !!oePeriodId,
      staleTime: 120000,
    },
  );
};

// Pulls every page of the Parent Renewal Dashboard for one OE period, for the
// Excel export. Deliberately not a hook: this runs on demand from a button.
//
// Page 1 is fetched first because its totalPages tells us how many remain; the
// rest go out in small concurrent batches. Bounded rather than all-at-once: each
// page still enriches its rows against Salesforce, and a season is a dozen-plus
// pages, so an unbounded fan-out would put that many simultaneous requests on a
// shared service to save a couple of seconds.
export const PARENT_RENEWAL_EXPORT_CONCURRENCY = 4;

export const fetchAllParentRenewalRows = async (
  oePeriodId,
  showAllClients,
  search,
  statusFilter,
  groupStatusFilter,
  sort = [],
) => {
  const PAGE_SIZE = 100;
  const MAX_PAGES = 200; // guard: never loop forever on an unexpected response

  const fetchPage = async (page) => {
    const response = await axiosGet(
      SERVICE_URLS.CLIENT_STATUS_REPORT_PARENT_PAGE(
        oePeriodId,
        showAllClients,
        page,
        PAGE_SIZE,
        sort,
        search,
        statusFilter,
        groupStatusFilter,
      ),
    );
    return response?.data?.page || null;
  };

  const first = await fetchPage(1);
  if (!first) return [];

  const totalPages = Math.min(first.totalPages || 1, MAX_PAGES);
  const items = [...(first.items || [])];

  const remaining = [];
  for (let page = 2; page <= totalPages; page += 1) remaining.push(page);

  // Keep pages in order regardless of which request settles first, so the export
  // does not silently reshuffle rows between runs.
  const byPage = new Map();
  for (let i = 0; i < remaining.length; i += PARENT_RENEWAL_EXPORT_CONCURRENCY) {
    const batch = remaining.slice(i, i + PARENT_RENEWAL_EXPORT_CONCURRENCY);
    const results = await Promise.all(batch.map((page) => fetchPage(page)));
    batch.forEach((page, idx) => byPage.set(page, results[idx]));
  }

  for (let page = 2; page <= totalPages; page += 1) {
    const payload = byPage.get(page);
    if (payload?.items) items.push(...payload.items);
  }

  return items;
};

// Pulls every page of the Assigned Clients grid for one OE period, for the
// Excel export. Mirrors fetchAllParentRenewalRows above, but forwards every
// active column filter (not just search) since Assigned Clients has seven of
// them -- an export that silently dropped them would show more rows than the
// filtered grid the user is looking at.
export const fetchAllClientsStatusReportRows = async (
  oePeriodId,
  showAllClients,
  search,
  sort = [],
  statusFilter,
  renewalFilter,
  brokerFilter,
  benefitRepFilter,
  salesRepFilter,
  carrierFilter,
  waveFilter,
) => {
  const PAGE_SIZE = 100;
  const MAX_PAGES = 200; // guard: never loop forever on an unexpected response

  const fetchPage = async (page) => {
    const response = await axiosGet(
      SERVICE_URLS.CLIENT_STATUS_REPORT_PAGE(
        oePeriodId,
        showAllClients,
        page,
        PAGE_SIZE,
        sort,
        search,
        statusFilter,
        renewalFilter,
        brokerFilter,
        benefitRepFilter,
        salesRepFilter,
        carrierFilter,
        waveFilter,
      ),
    );
    return response?.data || null;
  };

  const first = await fetchPage(1);
  if (!first) return [];

  const totalPages = Math.min(first.totalPages || 1, MAX_PAGES);
  const items = [...(first.items || [])];

  const remaining = [];
  for (let page = 2; page <= totalPages; page += 1) remaining.push(page);

  const byPage = new Map();
  for (let i = 0; i < remaining.length; i += PARENT_RENEWAL_EXPORT_CONCURRENCY) {
    const batch = remaining.slice(i, i + PARENT_RENEWAL_EXPORT_CONCURRENCY);
    const results = await Promise.all(batch.map((page) => fetchPage(page)));
    batch.forEach((page, idx) => byPage.set(page, results[idx]));
  }

  for (let page = 2; page <= totalPages; page += 1) {
    const payload = byPage.get(page);
    if (payload?.items) items.push(...payload.items);
  }

  return items;
};

export const useQueryGetParentRenewalPage = (
  oePeriodId,
  showAllClients,
  page,
  size,
  sort,
  search,
  statusFilter,
  groupStatusFilter,
) => {
  return useQuery(
    [
      QUERY_KEYS.GET_PARENT_RENEWAL_PAGE,
      oePeriodId,
      showAllClients,
      page,
      size,
      sort,
      search,
      statusFilter,
      groupStatusFilter,
    ],
    () =>
      axiosGet(
        SERVICE_URLS.CLIENT_STATUS_REPORT_PARENT_PAGE(
          oePeriodId,
          showAllClients,
          page,
          size,
          sort,
          search,
          statusFilter,
          groupStatusFilter,
        ),
      ),
    {
      keepPreviousData: true,
      enabled: !!oePeriodId,
      staleTime: 120000,
    },
  );
};

export const useQueryGetClients = (
  oePeriodId,
  showAllClients,
  isBdm = false,
) => {
  const normalizedShowAllClients =
    showAllClients === undefined ? "No" : showAllClients;

  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_LIST(oePeriodId, normalizedShowAllClients, isBdm),
    );
  };

  let enabled = false;
  if (oePeriodId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    refetchInterval: 120000,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.GET_CLIENTS, oePeriodId, normalizedShowAllClients, isBdm],
    getData,
    option,
  );
};

export const useQueryGetClient = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.CLIENT_DETAILS(oePeriodId, clientId));
  };

  let enabled = false;
  if (oePeriodId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 2,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    refetchInterval: 120000,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.GET_CLIENT, oePeriodId, clientId],
    getData,
    option,
  );
};

export const useQueryGetClientBenefitRep = (clientId) => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.GET_CLIENT_BENEFIT_REP(clientId));
  };

  let enabled = false;
  if (clientId) {
    enabled = true;
  }

  let option = {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.GET_CLIENT_BENEFIT_REP, clientId],
    getData,
    option,
  );
};

export const useQueryGetClientBroker = (clientId) => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.GET_CLIENT_BROKER(clientId));
  };

  let enabled = false;
  if (clientId) {
    enabled = true;
  }

  let option = {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    enabled: enabled,
  };

  return useQuery([QUERY_KEYS.GET_CLIENT_BROKER, clientId], getData, option);
};

export const useQueryGetSubmissionRecord = (clientId) => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.GET_CLIENT_SUBMISSION_RECORD(clientId));
  };

  let enabled = false;
  if (clientId) {
    enabled = true;
  }

  let option = {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.GET_CLIENT_SUBMISSION_RECORD, clientId],
    getData,
    option,
  );
};

export const useQueryGetClientSalesRep = (clientId) => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.GET_CLIENT_SALES_REP(clientId));
  };

  let enabled = false;
  if (clientId) {
    enabled = true;
  }

  let option = {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    enabled: enabled,
  };

  return useQuery([QUERY_KEYS.GET_CLIENT_SALES_REP, clientId], getData, option);
};

export const useQueryGetModelVersionGetData = async (oePeriodId, clientId) => {
  return await axiosGet(
    SERVICE_URLS.CLIENT_MODEL_VERSIONS(oePeriodId, clientId),
  );
};

export const useQueryGetModelVersions = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_MODEL_VERSIONS(oePeriodId, clientId),
    );
  };

  let enabled = false;
  if (oePeriodId && clientId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: false,
    enabled: enabled,
  };
  return useQuery(
    [QUERY_KEYS.GET_CLIENT_MODEL_VERSIONS, oePeriodId, clientId],
    getData,
    option,
  );
};

export const useQueryGetModelSummary = (
  oePeriodId,
  clientId,
  versionId,
  comparisonVersionId,
) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_MODEL_SUMMARY_GET(
        oePeriodId,
        clientId,
        versionId,
        comparisonVersionId,
      ),
    );
  };

  let enabled = false;
  if (oePeriodId && clientId && versionId && versionId !== "null") {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: false,
    enabled: enabled,
  };
  return useQuery(
    [
      QUERY_KEYS.GET_CLIENT_MODEL_SUMMARY,
      oePeriodId,
      clientId,
      versionId,
      comparisonVersionId,
    ],
    getData,
    option,
  );
};

export const useQueryGetModelAvailablePlans = (
  oePeriodId,
  clientId,
  versionId,
  groupId,
) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_MODEL_AVAILABLE_PLANS(
        oePeriodId,
        clientId,
        versionId,
        groupId,
      ),
    );
  };

  let enabled = false;
  if (oePeriodId && clientId && versionId && groupId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: false,
    enabled: enabled,
  };
  return useQuery(
    [
      QUERY_KEYS.GET_CLIENT_MODEL_AVAILABLE_PLANS,
      oePeriodId,
      clientId,
      versionId,
      groupId,
    ],
    getData,
    option,
  );
};

export const useQueryGetModelDetail = (oePeriodId, clientId, versionId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_MODEL_GET(oePeriodId, clientId, versionId),
    );
  };

  let enabled = false;
  if (oePeriodId && clientId && versionId && versionId !== "null") {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 3,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: false,
    enabled: enabled,
  };
  return useQuery(
    [QUERY_KEYS.GET_CLIENT_MODEL_DETAIL, oePeriodId, clientId, versionId],
    getData,
    option,
  );
};

export const useQueryGetClientPlans = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.CLIENT_PLANS_GET(oePeriodId, clientId));
  };

  let enabled = false;
  if (oePeriodId && clientId) {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 3,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: false,
    enabled: enabled,
  };
  return useQuery(
    [QUERY_KEYS.GET_CLIENT_PLANS, oePeriodId, clientId],
    getData,
    option,
  );
};

export const useQueryGetModelMaxContributions = (
  oePeriodId,
  clientId,
  versionId,
) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_MODEL_MAXCONTRIBS(oePeriodId, clientId, versionId),
    );
  };

  let enabled = false;
  if (oePeriodId && clientId && versionId && versionId !== "null") {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 3,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: false,
    enabled: enabled,
  };
  return useQuery(
    [QUERY_KEYS.GET_CLIENT_MODEL_MAXCONTRIBS, oePeriodId, clientId, versionId],
    getData,
    option,
  );
};

export const useQueryGetModelErrors = (oePeriodId, clientId, versionId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_MODEL_ERRORS_GET(oePeriodId, clientId, versionId),
    );
  };

  let enabled = false;
  if (oePeriodId && clientId && versionId && versionId !== "null") {
    enabled = true;
  }

  let option = {
    staleTime: 1000 * 60 * 3,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: false,
    enabled: enabled,
  };
  return useQuery(
    [QUERY_KEYS.GET_CLIENT_MODEL_ERRORS, oePeriodId, clientId, versionId],
    getData,
    option,
  );
};

export const useMutateSetMaxContribution = () => {
  const postData = async (data) => {
    return await axiosPut(SERVICE_URLS.CLIENT_MODEL_SET_MAX_CONTRIBUTION, data);
  };

  var queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_DETAIL,
        variables.oePeriodId,
        variables.clientId,
        variables.modelVersionId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_MAXCONTRIBS,
        variables.oePeriodId,
        variables.clientId,
        variables.modelVersionId,
      ]);
    },
  });
};

export const useMutateCopyContribution = () => {
  const postData = async (data) => {
    return await axiosPut(SERVICE_URLS.CLIENT_MODEL_COPY_CONTRIBUTION, data);
  };

  var queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_DETAIL,
        variables.oePeriodId,
        variables.clientId,
        variables.modelVersionId,
      ]);
    },
  });
};

export const useMutatePostOESummaryReadStatus = () => {
  const postData = async (data) => {
    return await axiosPut(SERVICE_URLS.CLIENT_UPDATE_OESUMMARY_STATAS, data);
  };

  var queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENTS,
        variables.oePeriodId,
      ]);
    },
  });
};

export const useMutateCreateModelVersion = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.CLIENT_CREATE_MODEL, data);
  };

  var queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_VERSIONS,
        variables.oePeriodId,
        variables.clientId,
      ]);
    },
  });
};

export const useMutateGetStarted = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.CLIENT_GET_STARTED, data);
  };

  var queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([variables.oePeriodId, variables.clientId]);
    },
  });
};

export const useMutateUpdateModelRates = () => {
  const postData = async (data) => {
    return await axiosPut(
      SERVICE_URLS.CLIENT_MODEL_UPDATE_RATE(
        data.oePeriodId,
        data.clientId,
        data.versionId,
      ),
      data.rates,
    );
  };

  var queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_SUMMARY,
        variables.oePeriodId,
        variables.clientId,
        variables.versionId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_DETAIL,
        variables.oePeriodId,
        variables.clientId,
        variables.versionId,
      ]);
    },
  });
};

export const useMutateModelAddPlan = () => {
  const postData = async (data) => {
    return await axiosPost(SERVICE_URLS.CLIENT_MODEL_ADD_PLAN, data);
  };

  var queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_AVAILABLE_PLANS,
        variables.oePeriodId,
        variables.clientId,
        variables.modelVersionId,
        variables.groupId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_SUMMARY,
        variables.oePeriodId,
        variables.clientId,
        variables.modelVersionId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_DETAIL,
        variables.oePeriodId,
        variables.clientId,
        variables.modelVersionId,
      ]);
      // Adding a plan can introduce a new medical carrier, and the Aetna submit
      // gate reads that list from cache (5 minute staleTime). Without this the
      // gate would be evaluated against the carriers present at page load.
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_RATE_MODEL_MEDICAL_CARRIERS,
        variables.oePeriodId,
        variables.clientId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_RATE_MODEL_SUBMIT_CARRIER_RULE,
        variables.oePeriodId,
        variables.clientId,
      ]);
    },
  });
};

export const acceptedDisclosure = async (data) => {
  let result = await axiosPost(SERVICE_URLS.CLIENT_MODEL_DISCLOSURE_ACCEPT, {
    oePeriodId: Number(data.oePeriodId),
    clientId: data.clientId,
    clientName: data.clientName,
    modelVersionId: data.versionId != null ? Number(data.versionId) : 0,
    disclosureAccepted: data.disclosureAccepted ? 1 : 0,
    disclosureDownloaded: data.downloaded,
    userAgent: data.userAgent,
    browserLanguage: data.browserLanguage,
    platform: data.platform,
    mobile: data.mobile,
    timezone: data.timezone,
    deviceFingerprint: data.deviceFingerprint,
    timeToAcceptSeconds: data.timeToAcceptSeconds,
    firstName: data.firstName,
    lastName: data.lastName,
    eeId: data.eeId,
    userType: data.userType,
    actorRole: data.actorRole,
    isInternalUser: data.isInternalUser ?? false,
    isProxyAcknowledgment: data.isProxyAcknowledgment ?? false,
    proxyAuthorizedBy: data.proxyAuthorizedBy ?? null,
  });
  return result;
};

export const useQueryGetDisclosureStatus = (oePeriodId, clientId) => {
  const getData = async () => {
    return await axiosGet(
      SERVICE_URLS.CLIENT_MODEL_DISCLOSURE_STATUS(oePeriodId, clientId),
    );
  };

  const enabled = !!(oePeriodId && clientId);

  return useQuery(
    [QUERY_KEYS.GET_CLIENT_DISCLOSURE_STATUS, oePeriodId, clientId],
    getData,
    {
      staleTime: 1000 * 60 * 2,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: false,
      enabled,
    },
  );
};

const invalidateClientWorkflowStatusQueries = (
  queryClient,
  oePeriodId,
  clientId,
) => {
  queryClient.invalidateQueries([QUERY_KEYS.GET_CLIENTS, oePeriodId]);
  queryClient.invalidateQueries([
    QUERY_KEYS.GET_CLIENTS_STATUS_REPORT,
    oePeriodId,
  ]);
  queryClient.invalidateQueries([QUERY_KEYS.GET_CLIENT, oePeriodId, clientId]);
};

export const useMutateSubmitModel = () => {
  const putData = async (data) => {
    return await axiosPut(
      SERVICE_URLS.CLIENT_MODEL_SUMIT_RATE(
        data.oePeriodId,
        data.clientId,
        data.versionId,
      ),
      data,
    );
  };

  var queryClient = useQueryClient();

  return useMutation(putData, {
    onSuccess: (_data, variables) => {
      invalidateClientWorkflowStatusQueries(
        queryClient,
        variables.oePeriodId,
        variables.clientId,
      );
    },
  });
};

export const useMutateOverrideClientOEStatus = () => {
  const putData = async (data) => {
    return await axiosPut(SERVICE_URLS.CLIENT_MODEL_OVERRIDE_STATUS, data);
  };

  const queryClient = useQueryClient();

  return useMutation(putData, {
    onSuccess: (_data, variables) => {
      invalidateClientWorkflowStatusQueries(
        queryClient,
        variables.oePeriodId,
        variables.clientId,
      );
    },
  });
};

export const updateClientModelName = async ({ modelVersionId, modelName }) => {
  const url = "/api/RenameClientRateModel/rename";

  const payload = {
    ModelVersionId: modelVersionId,
    NewModelName: modelName,
  };

  return axiosPost(url, payload);
};

export const useMutateUpdateModelName = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateClientModelName,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_MODEL_DETAIL,
        variables.oePeriodId,
        variables.clientId,
        variables.versionId,
      ]);
    },
  });
};

export const useMutateUnlockModel = () => {
  const putData = async (data) => {
    return await axiosPut(
      SERVICE_URLS.CLIENT_MODEL_UNLOCK_RATE(
        data.oePeriodId,
        data.clientId,
        data.versionId,
      ),
      data,
    );
  };

  var queryClient = useQueryClient();

  return useMutation(putData, {
    onSuccess: (_data, variables) => {
      invalidateClientWorkflowStatusQueries(
        queryClient,
        variables.oePeriodId,
        variables.clientId,
      );
    },
  });
};

export const useMutatePublishModel = () => {
  const putData = async (data) => {
    return await axiosPut(
      SERVICE_URLS.CLIENT_MODEL_PUBLISH_RATE(
        data.oePeriodId,
        data.clientId,
        data.versionId,
        data.isRetry,
      ),
      data,
    );
  };

  var queryClient = useQueryClient();

  return useMutation(putData, {
    onSuccess: (_data, variables) => {
      invalidateClientWorkflowStatusQueries(
        queryClient,
        variables.oePeriodId,
        variables.clientId,
      );
    },
  });
};

export const useMutateModelDeletePlan = () => {
  const postData = async (data) => {
    return await axiosDelete(SERVICE_URLS.CLIENT_MODEL_DELETE_PLAN, data);
  };

  var queryClient = useQueryClient();

  return useMutation(postData, {
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_AVAILABLE_PLANS,
        variables.oePeriodId,
        variables.clientId,
        variables.modelVersionId,
        variables.groupId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_SUMMARY,
        variables.oePeriodId,
        variables.clientId,
        variables.modelVersionId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.GET_CLIENT_MODEL_DETAIL,
        variables.oePeriodId,
        variables.clientId,
        variables.modelVersionId,
      ]);
      // Removing a plan can drop a medical carrier, so the cached carrier list
      // behind the Aetna submit gate is stale until this refetches.
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_RATE_MODEL_MEDICAL_CARRIERS,
        variables.oePeriodId,
        variables.clientId,
      ]);
      queryClient.invalidateQueries([
        QUERY_KEYS.CLIENT_RATE_MODEL_SUBMIT_CARRIER_RULE,
        variables.oePeriodId,
        variables.clientId,
      ]);
    },
  });
};

export const useQueryGetClientModelAuditRecords = (clientId) => {
  const getData = async () => {
    return await axiosGet(SERVICE_URLS.CLIENT_MODEL_AUDIT_RECORD(clientId));
  };

  let enabled = false;
  if (clientId) {
    enabled = true;
  }

  let option = {
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
    refetchInterval: 30000,
    enabled: enabled,
  };

  return useQuery(
    [QUERY_KEYS.GET_CLIENT_AUDIT_RECORDS, clientId],
    getData,
    option,
  );
};

export const useMutateUpdateModelSubClients = () => {
  const queryClient = useQueryClient();

  const putData = async (data) => {
    return await axiosPut(SERVICE_URLS.CLIENT_MODEL_UPDATE_SUB_CLIENTS, data);
  };

  return useMutation(putData, {
    onSuccess: async () => {
      await queryClient.refetchQueries([QUERY_KEYS.GET_CLIENT_MODEL_DETAIL]);
    },
  });
};
